import { c as _c } from "react/compiler-runtime";
import { Camera, ChevronDown } from "lucide-react";
import { useState } from "react";
import { FeatureFacts } from "@/lib/components/FeatureFacts";
import { ImageLightbox } from "@/lib/components/ImageLightbox";
import { DOOR_TYPES, DOOR_WIDTHS, FEATURE_LEVELS, formatDistanceMeters, getKeyFacts, PARKING_PAYMENT, PARKING_SPACE_TYPES, SURFACE_TYPES } from "@/lib/utils/accessibilityLabels";

/**
 * Shared feature card used across the app:
 * - Map sidebar (PlaceDetail)
 * - Place page (PlatsDetailView)
 * - Experience detail (ExperienceDetailView)
 *
 * Shows photo thumbnail (with lightbox), type name, key facts,
 * and expandable assessment details.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function FeatureCard(t0) {
  const $ = _c(46);
  const {
    feature
  } = t0;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const photo = feature.media?.[0]?.url;
  const a = feature.assessment?.data;
  const featureTypeName = feature.featureType?.name;
  const isParking = featureTypeName === "Parkering";
  const distanceMeters = feature.distanceMeters;
  let t1;
  if ($[0] !== a || $[1] !== distanceMeters || $[2] !== featureTypeName) {
    t1 = a ? getKeyFacts(a, featureTypeName, {
      distanceMeters
    }) : [];
    $[0] = a;
    $[1] = distanceMeters;
    $[2] = featureTypeName;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const facts = t1;
  let t2;
  if ($[4] !== feature.featureType?.name || $[5] !== photo) {
    t2 = photo ? /*#__PURE__*/_jsx("button", {
      onClick: () => setLightboxOpen(true),
      className: "shrink-0 overflow-hidden rounded-lg",
      children: /*#__PURE__*/_jsx("img", {
        src: photo,
        alt: feature.featureType?.name ?? "Entr\xE9",
        className: "h-14 w-14 object-cover transition-opacity hover:opacity-80"
      })
    }) : /*#__PURE__*/_jsx("div", {
      className: "flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-gray-100",
      children: /*#__PURE__*/_jsx(Camera, {
        className: "h-4 w-4 text-gray-300"
      })
    });
    $[4] = feature.featureType?.name;
    $[5] = photo;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== a || $[8] !== detailsOpen) {
    t3 = () => a && setDetailsOpen(!detailsOpen);
    $[7] = a;
    $[8] = detailsOpen;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = feature.featureType?.name ?? "Entr\xE9";
  let t5;
  if ($[10] !== t4) {
    t5 = /*#__PURE__*/_jsx("span", {
      className: "text-sm font-medium text-gray-700",
      children: t4
    });
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== feature.status) {
    t6 = feature.status === "pending" && /*#__PURE__*/_jsx("span", {
      className: "rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-amber-700",
      children: "V\xE4ntar"
    });
    $[12] = feature.status;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = /*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-2",
      children: [t5, t6]
    });
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== facts) {
    t8 = /*#__PURE__*/_jsx(FeatureFacts, {
      facts: facts
    });
    $[17] = facts;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== a) {
    t9 = !a && /*#__PURE__*/_jsx("p", {
      className: "mt-1 text-xs text-gray-400",
      children: "Ingen bed\xF6mning \xE4nnu"
    });
    $[19] = a;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t3 || $[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t10 = /*#__PURE__*/_jsxs("button", {
      onClick: t3,
      className: "min-w-0 flex-1 text-left",
      children: [t7, t8, t9]
    });
    $[21] = t3;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== a || $[27] !== detailsOpen) {
    t11 = a && /*#__PURE__*/_jsx("button", {
      onClick: () => setDetailsOpen(!detailsOpen),
      className: "flex shrink-0 items-center self-center rounded p-1 text-gray-400 hover:bg-gray-100",
      children: /*#__PURE__*/_jsx(ChevronDown, {
        className: `h-4 w-4 transition-transform ${detailsOpen ? "rotate-180" : ""}`
      })
    });
    $[26] = a;
    $[27] = detailsOpen;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t2) {
    t12 = /*#__PURE__*/_jsxs("div", {
      className: "flex gap-3 p-3",
      children: [t2, t10, t11]
    });
    $[29] = t10;
    $[30] = t11;
    $[31] = t2;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== a || $[34] !== detailsOpen || $[35] !== distanceMeters || $[36] !== isParking) {
    t13 = detailsOpen && a && /*#__PURE__*/_jsxs("div", {
      className: "border-t px-3 py-2.5",
      children: [isParking ? /*#__PURE__*/_jsxs("div", {
        className: "grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2",
        children: [/*#__PURE__*/_jsx(DetailRow, {
          label: "Typ av ruta",
          value: PARKING_SPACE_TYPES[a.parkingSpaceType] ?? "Ok\xE4nd"
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Avst\xE5nd till entr\xE9",
          value: formatDistanceMeters(distanceMeters)
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Skylt (rullstol)",
          value: boolLabel(a.hasWheelchairSign)
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Markm\xE5lning",
          value: boolLabel(a.hasGroundMarkings)
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Neds\xE4nkt kantsten",
          value: boolLabel(a.hasKerbCut)
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Tak \xF6ver",
          value: boolLabel(a.isCovered)
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Avgift",
          value: PARKING_PAYMENT[a.paymentType] ?? "Ok\xE4nd"
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Underlag",
          value: SURFACE_TYPES[a.surfaceType] ?? a.surfaceType
        })]
      }) : /*#__PURE__*/_jsxs("div", {
        className: "grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2",
        children: [/*#__PURE__*/_jsx(DetailRow, {
          label: "D\xF6rtyp",
          value: DOOR_TYPES[a.doorType] ?? a.doorType
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "D\xF6rrbredd",
          value: DOOR_WIDTHS[a.estimatedDoorWidth] ?? a.estimatedDoorWidth
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Tr\xF6skel",
          value: a.hasDoorThreshold ? "Ja" : "Nej"
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Trappsteg",
          value: a.hasSteps ? a.stepCount ? `${a.stepCount} st` : "Ja" : "Nej"
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Ramp",
          value: a.hasRamp ? "Ja" : "Nej"
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Handledare",
          value: a.hasHandrails ? "Ja" : "Nej"
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Auto d\xF6rr\xF6ppnare",
          value: a.hasAutomaticDoorOpener ? "Ja" : "Nej"
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Markyta",
          value: SURFACE_TYPES[a.surfaceType] ?? a.surfaceType
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Niv\xE5",
          value: FEATURE_LEVELS[a.entranceLevel] ?? a.entranceLevel
        }), /*#__PURE__*/_jsx(DetailRow, {
          label: "Kontrast",
          value: a.hasContrastMarkings ? "Ja" : "Nej"
        })]
      }), a.additionalNotes && /*#__PURE__*/_jsx("p", {
        className: "mt-1.5 border-t pt-1.5 text-xs italic text-gray-500",
        children: a.additionalNotes
      })]
    });
    $[33] = a;
    $[34] = detailsOpen;
    $[35] = distanceMeters;
    $[36] = isParking;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== feature.featureType?.name || $[39] !== lightboxOpen || $[40] !== photo) {
    t14 = photo && /*#__PURE__*/_jsx(ImageLightbox, {
      src: photo,
      alt: feature.featureType?.name,
      open: lightboxOpen,
      onClose: () => setLightboxOpen(false)
    });
    $[38] = feature.featureType?.name;
    $[39] = lightboxOpen;
    $[40] = photo;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t12 || $[43] !== t13 || $[44] !== t14) {
    t15 = /*#__PURE__*/_jsxs("div", {
      className: "overflow-hidden rounded-lg border bg-white",
      children: [t12, t13, t14]
    });
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
}
function DetailRow(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = /*#__PURE__*/_jsx("span", {
      className: "text-gray-500",
      children: label
    });
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = value ?? "\u2014";
  let t3;
  if ($[2] !== t2) {
    t3 = /*#__PURE__*/_jsx("span", {
      className: "font-medium text-gray-800",
      children: t2
    });
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t3) {
    t4 = /*#__PURE__*/_jsxs("div", {
      className: "flex justify-between gap-1",
      children: [t1, t3]
    });
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function boolLabel(v) {
  if (v === true) return "Ja";
  if (v === false) return "Nej";
  return "Okänt";
}